// Analytics tab › Processes: master-detail — the project's General Processes
// (G) on the left, the EXISTING embedded GenProcessDesignerPage on the right.
// A General Process is a batch run: source SQL → E/C/S/L calculations → rows
// posted into a target table or an existing transaction. "New" drafts one with
// AI (the shared transaction-class model generator) or creates a blank one.
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, Circle, ListPlus } from 'lucide-react'
import GenProcessDesignerPage from '@/pages/designers/GenProcessDesignerPage'
import { objectCatalog, type CatalogObject } from '@/api/designers'
import {
  listProjectProcesses, saveProcessObject, type ProjectProcessSummary,
} from '@/api/genprocess'
import { generateModel } from '@/api/generateapp'
import { blankProcess } from '@/pages/designers/genprocess/types'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import PublishToMenuDialog, { type PublishTarget } from './PublishToMenuDialog'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

export default function ProcessesSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [processes, setProcesses] = useState<ProjectProcessSummary[]>([])
  const [catalog, setCatalog] = useState<Record<string, CatalogObject>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuTarget, setMenuTarget] = useState<PublishTarget | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The list gives rich meta; the catalog adds used/unused. The catalog is
      // best-effort — the list alone is enough to render.
      const [listRes, catRes] = await Promise.allSettled([
        listProjectProcesses(project),
        objectCatalog(project, 'G'),
      ])
      if (listRes.status === 'rejected') throw listRes.reason
      setProcesses(listRes.value.processes || [])
      if (catRes.status === 'fulfilled') {
        const map: Record<string, CatalogObject> = {}
        for (const o of catRes.value.objects || []) map[o.obj_name] = o
        setCatalog(map)
      }
    } catch (err) {
      setError(errMessage(err, 'Could not load processes'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = processes.map((p) => {
    const cat = catalog[p.obj_name]
    const hasName = !!p.name && p.name !== p.obj_name
    const tags: string[] = []
    if (p.target) {
      tags.push(p.persist_mode === 'transaction'
        ? `posts to transaction ${p.target}`
        : `posts to ${p.target}`)
    }
    if (p.calculation_count > 0) {
      tags.push(`${p.calculation_count} calculation${p.calculation_count === 1 ? '' : 's'}`)
    }
    return {
      id: p.obj_name,
      name: p.name || p.obj_name,
      subtitle: hasName ? p.obj_name : undefined,
      description: p.description || undefined,
      tags: tags.length ? tags : undefined,
      badge: cat
        ? (cat.used
            ? { text: 'On the app menu / in use', tone: 'success' as const, icon: <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} /> }
            : { text: 'Not used yet', tone: 'muted' as const, icon: <Circle size={14} style={{ color: 'var(--color-text-faint)' }} /> })
        : undefined,
    }
  })

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await generateModel({
        obj_name: objName,
        obj_type: 'G',
        description: description || name,
        // The G generator reads the spec as its brief; the description IS the
        // brief here (this section has no separate FRS entry).
        functional_specs: description || name,
        project_folder: project,
      })
      toast.success(`Process "${name}" drafted — open it to check the source SQL and calculations`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the process'))
      return null
    }
  }

  // Manual path: persist a blank process (no AI) and open the designer so the
  // user writes the source SQL, calculations and target themselves.
  const handleCreateManual = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      const proc = blankProcess(objName)
      proc.name = name.trim() || objName
      proc.description = description.trim()
      await saveProcessObject(project, objName, proc)
      toast.success(`Process "${name}" created — add its source SQL and calculations`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the process'))
      return null
    }
  }

  return (
    <>
      <ObjectMasterDetail
        project={project}
        typeCode="G"
        heading="Processes"
        subCopy="General Processes turn a query into posted rows: source SQL → row calculations (expression, conditional, SQL, lookup) → reviewed and written into a target table or an existing transaction."
        items={items}
        loading={loading}
        error={error}
        onRetry={() => void refresh()}
        newLabel="New process"
        newDialogTitle="New process"
        namePlaceholder="e.g. Monthly Commission Run"
        descPlaceholder="e.g. Read posted invoices for the selected month, apply the commission slab per salesperson, and post the result as commission transactions."
        createButtonLabel="Generate with AI"
        onCreate={handleCreate}
        onCreateManual={handleCreateManual}
        manualButtonLabel="Create manually"
        onObjectSaved={() => { void refresh(); onChanged?.() }}
        registerLeaveGuard={registerLeaveGuard}
        initialObjName={initialObjName}
        openNonce={openNonce}
        detailToolbar={(objName) => {
          const cat = catalog[objName]
          if (!cat || cat.in_navigation) return null
          const p = processes.find((x) => x.obj_name === objName)
          return (
            <button
              className="btn btn-secondary btn-sm"
              title="Wire this process into the application menu — no regeneration"
              onClick={() => setMenuTarget({
                obj_name: objName,
                obj_type: 'G',
                title: p?.name || cat.title || objName,
              })}
            >
              <ListPlus size={13} />
              Add to menu…
            </button>
          )
        }}
        emptyState={(openNew) => (
          <EmptyStateCoach
            icon={<TypeChip code="G" size={26} />}
            title="No processes yet"
            body="A General Process runs a query, calculates new columns row by row, lets you review the result and posts it — a commission run, a revaluation, a period close. Draft one from a description, or build it step by step."
            action={
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                + New process
              </button>
            }
          />
        )}
        renderDesigner={(objName, common) => (
          <GenProcessDesignerPage key={`${project}:${objName}`} {...common} />
        )}
      />
      <PublishToMenuDialog
        open={menuTarget !== null}
        mode="menu"
        project={project}
        targets={menuTarget ? [menuTarget] : []}
        onClose={() => setMenuTarget(null)}
        onPublished={() => { void refresh(); onChanged?.() }}
      />
    </>
  )
}
